import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
  Stack,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { TrendingUp, Apartment, Group } from "@mui/icons-material";
import SarIcon from "../../icons/SarIcon";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useNavigate } from "react-router-dom";
import { useGetAdminDashboardQuery } from "../../../redux/api/authApi";
import { resolveInvestorUploadUrl } from "../../investor/myInvestmentsMapper";
import { dashboardShell } from "../../../theme/dashboardShell";

const shellAdmin = dashboardShell.admin;

function formatSarCompact(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e9) return `SAR ${(num / 1e9).toFixed(1)} B`;
  if (num >= 1e6) return `SAR ${(num / 1e6).toFixed(1)} M`;
  if (num >= 1e3) return `SAR ${(num / 1e3).toFixed(1)} K`;
  return `SAR ${num.toLocaleString()}`;
}

function unwrapDashboardPayload(res) {
  if (!res) return null;
  if (res.data && typeof res.data === "object" && !Array.isArray(res.data)) {
    return res.data;
  }
  return res;
}

const sxStyles = {
  page: {
    bgcolor: shellAdmin.pageBg,
    borderRadius: 2,
    p: { xs: 2, sm: 3 },
    mx: { xs: -1, sm: 0 },
  },
  statCard: {
    borderRadius: 2,
    boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
    border: "1px solid #e8eaed",
    height: "100%",
    bgcolor: "#fff",
  },
  reviewCard: {
    borderRadius: "14px",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.04)",
    border: "1px solid #e5e7eb",
    bgcolor: "#ffffff",
    height: "100%",
  },
  chipPending: {
    bgcolor: "#fef3c7",
    color: "#b45309",
    fontWeight: 600,
    fontSize: 11,
    height: 22,
  },
  chipCount: {
    bgcolor: "#fee2e2",
    color: "#dc2626",
    fontWeight: 600,
    fontSize: 11,
    height: 22,
  },
  viewBtn: {
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    color: shellAdmin.accent,
    minWidth: "auto",
    px: 1,
    "&:hover": { bgcolor: shellAdmin.accentSoftHoverBg },
  },
  /** Figma: white row, thin gray border */
  row: {
    p: 2,
    borderRadius: "12px",
    bgcolor: "#ffffff",
    border: "1px solid #e5e7eb",
  },
  avatar: { width: 44, height: 44, fontSize: 16, fontWeight: 700 },
};

function assetListImageUrl(item) {
  const spv = item?.spvDocument;
  if (spv?.fileUrl && /png|jpe?g|jpeg|webp|gif/i.test(String(spv.fileType || ""))) {
    return resolveInvestorUploadUrl(spv.fileUrl);
  }
  return "";
}

export default function AdminMainPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useGetAdminDashboardQuery();

  const payload = useMemo(() => unwrapDashboardPayload(data), [data]);
  const pendingReviews = payload?.pendingReviews ?? {};
  const kycBlock = pendingReviews.kycVerifications ?? {};
  const assetBlock = pendingReviews.assetApprovals ?? {};

  const kycItems = Array.isArray(kycBlock.items) ? kycBlock.items : [];
  const assetItems = Array.isArray(assetBlock.items) ? assetBlock.items : [];

  const stats = useMemo(() => {
    const summary = payload?.summary ?? {};
    return [
      {
        key: "users",
        title: "Total Users",
        value: String(summary.totalUsers?.value ?? "—"),
        icon: <Group sx={{ color: (theme) => theme.palette.statIcon.users.color }} />,
      },
      {
        key: "assets",
        title: "Active Assets",
        value: String(summary.activeAssets?.value ?? "—"),
        icon: <Apartment sx={{ color: (theme) => theme.palette.statIcon.assets.color }} />,
      },
      {
        key: "volume",
        title: "Platform Volume",
        value: formatSarCompact(summary.platformVolumeSar?.value),
        icon: <TrendingUp sx={{ color: (theme) => theme.palette.statIcon.volume.color }} />,
      },
      {
        key: "revenue",
        title: "Total Assets",
        value: String(summary.totalAssets?.value ?? "—"),
        icon: <SarIcon sx={{ color: (theme) => theme.palette.statIcon.revenue.color }} />,
      },
    ];
  }, [payload]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    const msg =
      error?.data?.message ||
      error?.error ||
      (typeof error === "string" ? error : "Could not load dashboard.");
    return (
      <Alert severity="error" onClose={() => refetch()} sx={{ borderRadius: 2 }}>
        {msg}
      </Alert>
    );
  }

  return (
    <Box sx={sxStyles.page}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ color: "#0f172a", letterSpacing: -0.5 }}
        >
          Platform Overview
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
          Monitor and manage your tokenization platform
        </Typography>
      </Box>

      <Grid container spacing={2.5} mb={3}>
        {stats.map((s) => (
          <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={s.key}>
            <Card sx={sxStyles.statCard} elevation={0}>
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  p: 2.5,
                  "&:last-child": { pb: 2.5 },
                }}
              >
                <Box sx={{ minWidth: 0, pr: 1 }}>
                  <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                    {s.title}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: "#0f172a", mt: 0.75, lineHeight: 1.2 }}
                  >
                    {s.value}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    bgcolor: (theme) => theme.palette.statIcon[s.key].bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {s.icon}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} alignItems="stretch">
        {/* Card 1 — Pending Reviews (KYC) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={sxStyles.reviewCard} elevation={0}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 }, "&:last-child": { pb: { xs: 2.5, sm: 3 } } }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 22, color: "#ca8a04" }} />
                <Typography variant="h6" fontWeight={700} sx={{ color: "#0f172a", fontSize: "1.05rem" }}>
                  Pending Reviews
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 2.5, fontSize: 14 }}>
                Items requiring immediate attention
              </Typography>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                mb={2}
              >
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <ShieldOutlinedIcon sx={{ fontSize: 20, color: shellAdmin.accent }} />
                  <Typography fontSize={15} fontWeight={600} sx={{ color: "#0f172a" }}>
                    KYC Verifications
                  </Typography>
                  <Chip
                    label={`${kycBlock.pendingCount ?? kycItems.length} Pending`}
                    size="small"
                    sx={sxStyles.chipCount}
                  />
                </Stack>
                <Button
                  variant="text"
                  sx={sxStyles.viewBtn}
                  onClick={() => navigate("/admin/users")}
                >
                  View All
                </Button>
              </Stack>
              <Stack spacing={1.5}>
                {kycItems.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "#94a3b8", py: 1 }}>
                    No pending KYC items.
                  </Typography>
                ) : (
                  kycItems.map((k) => (
                    <Stack
                      key={k._id || k.email}
                      direction="row"
                      spacing={1.75}
                      alignItems="center"
                      sx={sxStyles.row}
                    >
                      <Avatar sx={{ ...sxStyles.avatar, bgcolor: "#f1f5f9", color: "#475569" }} src={k.avatarUrl}>
                        {String(k.name || "?").charAt(0).toUpperCase()}
                      </Avatar>
                      <Box flex={1} minWidth={0}>
                        <Typography fontWeight={600} fontSize={14} sx={{ color: "#111827" }}>
                          {k.name || "—"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6b7280", fontSize: 13 }}>
                          {k.email || "—"}
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end" spacing={0.75} flexShrink={0}>
                        <Chip
                          label={k.kycStatus || "Pending"}
                          size="small"
                          sx={sxStyles.chipPending}
                        />
                        <Typography variant="caption" sx={{ color: "#9ca3af", fontSize: 12 }}>
                          {k.relativeTime || "—"}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 2 — Asset Approvals (same chrome as Pending Reviews) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={sxStyles.reviewCard} elevation={0}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 }, "&:last-child": { pb: { xs: 2.5, sm: 3 } } }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 22, color: "#ca8a04" }} />
                <Typography variant="h6" fontWeight={700} sx={{ color: "#0f172a", fontSize: "1.05rem" }}>
                  Asset Approvals
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 2.5, fontSize: 14 }}>
                Listings awaiting your review
              </Typography>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                mb={2}
              >
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <DescriptionOutlinedIcon sx={{ fontSize: 20, color: shellAdmin.accent }} />
                  <Typography fontSize={15} fontWeight={600} sx={{ color: "#0f172a" }}>
                    Pending assets
                  </Typography>
                  <Chip
                    label={`${assetBlock.pendingCount ?? assetItems.length} Pending`}
                    size="small"
                    sx={sxStyles.chipCount}
                  />
                </Stack>
                <Button
                  variant="text"
                  sx={sxStyles.viewBtn}
                  onClick={() => navigate("/admin/assets")}
                >
                  View All
                </Button>
              </Stack>
              <Stack spacing={1.5}>
                {assetItems.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "#94a3b8", py: 1 }}>
                    No pending asset approvals.
                  </Typography>
                ) : (
                  assetItems.map((a) => {
                    const img = assetListImageUrl(a);
                    const initial = String(a.assetName || "?").charAt(0).toUpperCase();
                    return (
                      <Stack
                        key={a._id || a.assetName}
                        direction="row"
                        spacing={1.75}
                        alignItems="center"
                        sx={sxStyles.row}
                      >
                        <Avatar
                          variant="circular"
                          src={img || undefined}
                          alt=""
                          sx={{
                            width: 44,
                            height: 44,
                            bgcolor: "#f1f5f9",
                            color: "#475569",
                            fontWeight: 700,
                          }}
                        >
                          {initial}
                        </Avatar>
                        <Box flex={1} minWidth={0}>
                          <Typography fontWeight={600} fontSize={14} sx={{ color: "#111827" }}>
                            {a.assetName || "—"}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#6b7280", fontSize: 13 }}>
                            {a.submittedBy ? `by ${a.submittedBy}` : a.ownerEmail || "—"}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.75} flexShrink={0}>
                          <Typography fontWeight={700} fontSize={14} sx={{ color: "#111827" }}>
                            {formatSarCompact(a.valueSar)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#9ca3af", fontSize: 12 }}>
                            {a.relativeTime || "—"}
                          </Typography>
                        </Stack>
                      </Stack>
                    );
                  })
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
